import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Truck, Wrench, Loader2, ImagePlus, ImageOff } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';
import { EquipmentKindImage } from '../components/work-permits/wizard/EquipmentKindImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function EqTypesPage() {
  const { canCreate, canEdit, canDelete, user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [subtypes, setSubtypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formMainType, setFormMainType] = useState('arac');
  const [formKey, setFormKey] = useState('');
  const [formLabel, setFormLabel] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const res = await axios.get(API + '/equipment-subtypes');
      setSubtypes(res.data);
    } catch (e) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  function openDialog(item) {
    if (item) {
      setEditing(item);
      setFormMainType(item.main_type);
      setFormKey(item.subtype_key);
      setFormLabel(item.subtype_label);
    } else {
      setEditing(null);
      setFormMainType('arac');
      setFormKey('');
      setFormLabel('');
    }
    setDialogOpen(true);
  }

  async function saveItem() {
    if (!formLabel) { toast.error('Alt tür adı zorunlu'); return; }
    // Kod'u otomatik olarak label'dan türet
    const autoKey = formLabel.toLowerCase().replace(/\s+/g, '_').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i');
    const payload = { main_type: formMainType, subtype_key: editing ? formKey : autoKey, subtype_label: formLabel, required_documents: [] };
    try {
      if (editing) { await axios.put(API + '/equipment-subtypes/' + editing.id, payload); toast.success('Güncellendi'); }
      else { await axios.post(API + '/equipment-subtypes', payload); toast.success('Eklendi'); }
      setDialogOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata oluştu'); }
  }

  async function uploadImage(item, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try { await axios.post(`${API}/equipment-subtypes/${item.id}/image`, fd); toast.success('Görsel yüklendi'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Görsel yüklenemedi'); }
  }

  async function removeImage(item) {
    try { await axios.delete(`${API}/equipment-subtypes/${item.id}/image`); toast.success('Görsel kaldırıldı'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaldırılamadı'); }
  }

  async function deleteItem(item) {
    if (!(await confirmDialog({ title: 'Kayıt silinsin mi?', description: 'Silmek istediğinize emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(API + '/equipment-subtypes/' + item.id); toast.success('Silindi'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const aracList = subtypes.filter(s => s.main_type === 'arac');
  const isekList = subtypes.filter(s => s.main_type === 'is_ekipmani');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ekipman Türleri</h1>
          <p className="text-slate-500">Alt türleri tanımlayın</p>
        </div>
        {canCreate('equipment') && <Button data-testid="add-subtype-btn" onClick={() => openDialog(null)}><Plus className="h-4 w-4 mr-2" />Alt Tür Ekle</Button>}
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-blue-600" />A Grubu – Firma ve operatörüyle gelen araç / iş makineleri<span className="text-xs font-normal text-slate-500">(Periyodik Muayene + Mali Mesuliyet)</span></CardTitle></CardHeader>
        <CardContent>{renderTable(aracList)}</CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Wrench className="h-5 w-5 text-orange-600" />B Grubu – İş ekipmanları<span className="text-xs font-normal text-slate-500">(Periyodik Muayene + opsiyonel Bakım)</span></CardTitle></CardHeader>
        <CardContent>{renderTable(isekList)}</CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl [&>*]:min-w-0" data-testid="subtype-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Düzenle' : 'Yeni Alt Tür'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 min-w-0">
            <div className="space-y-2">
              <Label>Ana Tür</Label>
              <Select value={formMainType} onValueChange={setFormMainType} disabled={!!editing}>
                <SelectTrigger className="w-full min-w-0 [&>span]:truncate [&>span]:min-w-0" data-testid="subtype-main-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-w-[min(36rem,90vw)]"><SelectItem value="arac">A Grubu – Firma ve operatörüyle gelen (Periyodik Muayene + Mali Mesuliyet)</SelectItem><SelectItem value="is_ekipmani">B Grubu – Sadece Periyodik Muayene (+ opsiyonel Bakım)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alt Tür Adı</Label>
              <Input data-testid="subtype-label-input" value={formLabel} onChange={e => setFormLabel(e.target.value)} placeholder="Vinç" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button data-testid="save-subtype-btn" onClick={saveItem}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderTable(items) {
    if (items.length === 0) return <p className="text-slate-400 text-center py-4">Henüz tanımlanmamış</p>;
    return (
      <table className="w-full">
        <thead><tr className="border-b"><th className="text-left py-2 font-medium w-20">Görsel</th><th className="text-left py-2 font-medium">Alt Tür</th><th className="text-right py-2 font-medium">İşlemler</th></tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id} className="border-b last:border-0" data-testid={`subtype-row-${item.subtype_key}`}>
              <td className="py-2"><EquipmentKindImage kind={item} index={i} /></td>
              <td className="py-3 font-medium">{item.subtype_label}</td>
              <td className="py-3 text-right">
                {isSuper && (
                  <label className="inline-flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sky-700 hover:bg-sky-50" title="Görsel yükle (PNG/JPG/WEBP/SVG, en fazla 2 MB)" data-testid={`subtype-image-upload-${item.subtype_key}`}>
                    <ImagePlus className="h-4 w-4" />
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => { uploadImage(item, e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                )}
                {isSuper && item.image_path && <Button variant="ghost" size="sm" className="text-slate-500" title="Görseli kaldır" onClick={() => removeImage(item)} data-testid={`subtype-image-remove-${item.subtype_key}`}><ImageOff className="h-4 w-4" /></Button>}
                {canEdit('equipment') && <Button variant="ghost" size="sm" onClick={() => openDialog(item)}><Pencil className="h-4 w-4" /></Button>}
                {canDelete('equipment') && <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => deleteItem(item)}><Trash2 className="h-4 w-4" /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

export default EqTypesPage;
